<template>
  <div>
    <Header @update="update" :totalCount="totalApps" />
    <div class="text-right"><b>Total Apps:</b> {{ totalApps }}</div>
    <b-table :items="integrations" :fields="fields" :busy="isLoading" show-empty bordered striped head-variant="dark"
      style="text-align: left" @row-clicked="showAppDetails">
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Loading...</strong>
        </div>
      </template>
      <template #empty>
        <div class="text-center text-danger my-2">No data found</div>
      </template>

      <template #cell(name)="row">
        <div style="width: 200px">
          <div style="display: flex">
            <div style="padding-bottom: 0.5rem" class="truncate">
              <b class="toCapitalFirst">{{ row.item.name }}</b>
            </div>
          </div>
          <div>Ver. {{ row.item.version ? row.item.version : 1 }}</div>
        </div>
      </template>

      <template #cell(submittedOn)="row">
        <div style="min-width: 100px">
          {{ formatTime(row.item.submittedOn) }}
        </div>
      </template>

      <template #cell(isWhiteLabelFriendly)="row">
        <div style="min-width: 100px">
          {{ row.item.isWhiteLabelFriendly === false ? "Standard" : "Whitelabel Friendly" }}
        </div>
      </template>

      <template #cell(userTypes)="row">
        <div style="min-width: 200px">
          {{ formatUserTypes(row.item.userTypes) }}
        </div>
      </template>

      <template #cell(allowedScopes)="row">
        <div style="display: flex; min-width: 200px; width: 300px; flex-wrap: wrap; gap: 0.25rem">
          <div v-if="row && row.item && row.item.allowedScopes && row.item.allowedScopes.length > 0"
            v-for="(scope, index) in row.item.allowedScopes" style="">
            <div class="tag info" style="padding-left: 0.5rem; padding-right: 0.5rem" v-if="index < 3">
              {{ scope }}
            </div>
            <div v-else-if="index === 3">
              {{
                row.item.allowedScopes.length - 3 > 0
                  ? "+ " + (row.item.allowedScopes.length - 3) + " more"
                  : ""
              }}
            </div>
          </div>
        </div>
      </template>

      <template #cell(sla)="row">
        <div style="min-width: 100px">
          {{ row.item.sla ? row.item.sla + "Days" : getSLA(row.item.submittedOn) }}
        </div>
      </template>

      <template #cell(status)="row">
        <div style="min-width: 150px">
          <div v-if="row.item.status === 'disapproved'" class="tag danger">Disapproved</div>
          <div v-else-if="row.item.approvedOn" style="padding-bottom: 0.5rem">
            <div class="tag success">Approved</div>
          </div>
          <div v-else class="tag warning">Pending Review</div>
        </div>
        <div>{{ formatTime(row.item.approvedOn || row.item.disapprovedOn) }}</div>
      </template>
      <template #cell(installations)="row">
        <div style="min-width: 100px;">
          {{ (row.item.oauthClient?.totalInstallations || 0) }}
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import * as moment from "moment";
import { MarketplaceService } from "@/services";
import { ReviewStatus } from "@/constants/Oauth.ts";
import Header from "@/components/marketplace/marketplaceAppsReviewPortal/Header.vue";
require("./style.css");

export default {
  components: { Header },
  data() {
    return {
      fields: [
        { key: "name", label: "AppName", sortable: true },
        { key: "submittedOn", label: "Submitted on", sortable: true },
        { key: "isWhiteLabelFriendly", label: "Listing Type" },
        { key: "userTypes", label: "Distribution type" },
        { key: "allowedScopes", label: "Scopes" },
        { key: "sla", label: "SLA" },
        { key: "status", label: "Status" },
        { key: "changeType", label: "Change Type" },
        { key: "isLCCertified", label: "LCCertified" },
        { key: "installations", label: "Installations" }
      ],
      showAllScopes: false,
      integrations: [],
      isLoading: false,
      totalApps: 0,
      limit: 20,
      skip: 0,
      status: ReviewStatus.REVIEW,
      searchText: "",
    };
  },
  async mounted() {
    await this.update();
  },
  methods: {
    async fetchData() {
      try {
        this.isLoading = true;
        let query = {
          skip: this.skip,
          limit: this.limit,
        };
        if (this.searchText) {
          query["query"] = this.searchText;
        }
        this.integrations = [];
        let response;
        if (this.status === "approved") {
          response = await MarketplaceService.Review.getReviewedApps(query);
          this.integrations = response.data.apps || [];
          this.totalApps = response.data.count || 0;
        } else {
          query["status"] =
            this.status === "disapproved" ? ReviewStatus.DISAPPROVED : ReviewStatus.REVIEW;
          response = await MarketplaceService.Review.getAppsForReview(query);
          this.integrations = response.data.apps || [];
          this.totalApps = response.data.count || 0;
        }
        // Add the "changeType" based on the presence of the `oauth` key
        this.integrations = this.integrations.map((app) => ({
          ...app,
          changeType: app.oauthClient ? "Updated Existing App" : "Added New App",
          isLCCertified: (app.oauthClient?.isLCCertified || app?.isLCCertified) ? "Yes" : "No",
        }));
      } catch (error) {
        console.error(error);
        alert(error.message || error.message[0] || "Something went wrong");
      } finally {
        this.isLoading = false;
      }
    },
    async update(limit = 20, skip = 0, status = ReviewStatus.REVIEW, searchText = "") {
      this.status = status;
      this.limit = limit;
      this.skip = skip;
      this.searchText = searchText;
      await this.fetchData();
    },
    formatTime(time) {
      return time ? moment(time).format("MMM DD, YYYY") : "";
    },
    formatUserTypes(userTypes) {
      return userTypes.length ? userTypes.join(", ") : "";
    },
    showAppDetails(item, index, event) {
      const type =
        (this.status === "approved"
          ? "Approved"
          : this.status === "disapproved"
            ? "Disapproved"
            : "Pending Review") || "Pending Review";
      this.$store.commit("oauth_client_app/set", item);
      this.$router.push(`/marketplace-apps-review/new/${item.appId || item._id}?type=${type}`);
    },
    getSLA(d) {
      if (d) {
        const diff = Math.ceil((new Date().getTime() - new Date(d).getTime()) / (1000 * 3600 * 24));
        return diff + " Days";
      }
      return "-";
    },
  },
};
</script>
